import { useProfile } from '@/hooks/useProfile';
import { useRequiredData } from '@/hooks/useRequiredData';
import { useIsAgent, useIsManager } from '@/hooks/useRoles';
import { ROUTES } from '@/routes/paths';
import type { RequiredField } from '@/api/services/OrderService';

export interface OnboardingStep {
  /** Ключ шага (i18n: app.home.firstSteps.step.<key>). */
  readonly key: string;
  /** Диплинк в нужный раздел «Моей компании». */
  readonly to: string;
  /** Данные шага заполнены (поля нет в списке недостающих). */
  readonly done: boolean;
  /** Финальный шаг «Аттестация» заблокирован, пока не готовы данные выше. */
  readonly locked: boolean;
  /** Финальный шаг (аттестация). */
  readonly final: boolean;
}

export interface Onboarding {
  /** Показывать карточку «Первые шаги» (и скрывать жёлтую полосу на «Главной»). */
  readonly active: boolean;
  /** Ещё неизвестно, нужен ли онбординг: плитки разделов показывать рано. */
  readonly pending: boolean;
  readonly steps: readonly OnboardingStep[];
  readonly doneCount: number;
  readonly totalCount: number;
}

interface StepDef {
  readonly key: string;
  readonly fields: readonly RequiredField[];
  readonly to: string;
  readonly final?: boolean;
}

const EMPTY: Onboarding = { active: false, pending: false, steps: [], doneCount: 0, totalCount: 0 };

// Онбординг нового пользователя портала. Показываем только владельцу-менеджеру неаттестованной
// организации, который НЕ агент (у агента нет аттестации и онбординга). Шаги — фиксированный набор
// обязательных данных из useRequiredData (get_banners_data), аттестация — финальный шаг. Один
// источник правды: карточка «Первые шаги» на «Главной» и скрытие жёлтой полосы там же берут отсюда.
export function useOnboarding(): Onboarding {
  const { data: profile, isLoading: profileLoading } = useProfile();
  const isManager = useIsManager();
  const isAgent = useIsAgent();
  const required = useRequiredData();

  // Владелец-менеджер, НЕ агент, организация ещё не аттестована, данные баннера успешно загружены.
  const eligible = isManager && !isAgent && profile != null && profile.isVerified !== true;
  if (!eligible || !required.isSuccess || required.data == null) {
    const pending = !isAgent && (profileLoading || (eligible && required.isLoading));
    return pending ? { ...EMPTY, pending } : EMPTY;
  }

  const missing = new Set<RequiredField>(required.data.requiredUserDataFields);

  // «Данные руководителя» есть у ЮЛ; у ИП/самозанятого руководителя нет. Если тип неизвестен, но поле
  // реально в недостающих — шаг всё равно показываем (не прячем требуемое).
  const managementRequired = missing.has('MANAGEMENT_NAME') || missing.has('MANAGEMENT_POST');
  const includeManagement = profile.type === 'LEGAL_ENTITY' || managementRequired;

  const defs: StepDef[] = [
    { key: 'taxSystem', fields: ['TAX_SYSTEM'], to: `${ROUTES.APP_COMPANY}#tax` },
    { key: 'bank', fields: ['BANK_REQUISITS'], to: `${ROUTES.APP_COMPANY_BANKING}?createNew=true` },
    { key: 'contact', fields: ['CONTACT_PHONE'], to: `${ROUTES.APP_COMPANY}?addContact=true` },
    { key: 'address', fields: ['ADDRESS'], to: `${ROUTES.APP_COMPANY}#addresses` },
    ...(includeManagement
      ? [
          {
            key: 'management',
            fields: ['MANAGEMENT_NAME', 'MANAGEMENT_POST'] as RequiredField[],
            to: `${ROUTES.APP_COMPANY}#management`,
          },
        ]
      : []),
    { key: 'certification', fields: ['CERTIFICATION'], to: ROUTES.APP_COMPANY_ACCREDITATION, final: true },
  ];

  // Аттестация зависит от данных (в т.ч. системы налогообложения — предусловие 424). Разблокируем её,
  // когда готовы все шаги-данные.
  const dataDone = defs.filter((d) => !d.final).every((d) => !d.fields.some((f) => missing.has(f)));

  const steps: OnboardingStep[] = defs.map((d) => {
    const done = !d.fields.some((f) => missing.has(f));
    return {
      key: d.key,
      to: d.to,
      done,
      final: d.final === true,
      locked: d.final === true && !dataDone,
    };
  });

  return {
    active: true,
    pending: false,
    steps,
    doneCount: steps.filter((s) => s.done).length,
    totalCount: steps.length,
  };
}
